import React, { useState } from 'react';
import { ISubjectListItem } from './types';
import {
  FieldType,
  ISchemaDataModelFieldsFragment,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import {
  RequiredFieldRow,
  SubjectNameWrapperDiv,
} from './DataModelerRequiredFieldsUI';
import { DataModelerSelectTableColumn } from '../DataModelerFieldDropdown';
import {
  NameWithIconWrapper,
  SpacedConfigurationSeparator,
  UnderTableHeaderDiv,
} from '../DataModelerCommonUI';
import EditableTextArea from '../../../../components/EditableTextArea';
import { ZodEffects, ZodObject, ZodString } from 'zod';
import {
  FieldError,
  FieldLabel,
  InputField,
} from '../../../../components/Form';
import { dataModelLabelInfoTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import InformationalTooltip from '../../../../components/InformationalTooltip';
import { validationErrorToString } from '../validation';
import { SquareRemoveButton, TextButton } from '../../../../components/Button';

interface IProps {
  isEditMode: boolean;
  schemaObj: ISchemaDataModelFieldsFragment;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
  subjectRequired: boolean;
  subjectsList: ISubjectListItem[];
  addNewSubject: (
    subjectName: string,
    tableColumn: ITableColumnFieldsFragment,
  ) => void;
  removeNewSubject: (subjectKey: string) => void;
  updateSubjectColumn: (
    subjectKey: string,
    tableKey: string,
    tableColumn: ITableColumnFieldsFragment,
  ) => void;
  subjectNameValidatorFunc: (
    subjectKey: string,
  ) => ZodObject<{ value: ZodEffects<ZodString, string, string> }>;
  suggestedNextSubjectName: string;
  updateSubjectName: (subjectKey: string, subjectName: string) => void;
}

const DataModelerSubjectsSection = ({
  isEditMode,
  schemaObj,
  tableKey,
  availableColumnsFromServer,
  subjectRequired,
  subjectsList,
  addNewSubject,
  removeNewSubject,
  updateSubjectColumn,
  subjectNameValidatorFunc,
  suggestedNextSubjectName,
  updateSubjectName,
}: IProps) => {
  const [draftSubjectName, setDraftSubjectName] = useState<string | null>(null);
  const [draftSubjectNameError, setDraftSubjectNameError] = useState<
    string | null
  >(null);
  const validator = subjectNameValidatorFunc('');
  const handleAddNewSubject = () => {
    if (!isEditMode) {
      return;
    }
    setDraftSubjectName(suggestedNextSubjectName);
    setDraftSubjectNameError(null);
  };

  const onDraftSubjectNameChange = (newVal: string) => {
    const parseRes = validator.safeParse({ value: newVal });
    if (parseRes.success) {
      setDraftSubjectNameError(null);
    } else {
      setDraftSubjectNameError(validationErrorToString(parseRes.error));
    }
    setDraftSubjectName(newVal);
  };

  return (
    <>
      <SpacedConfigurationSeparator layout="horizontal" size="full" />
      <RequiredFieldRow $noTopGap={true}>
        <UnderTableHeaderDiv>
          <span>Subject</span>
          <InformationalTooltip
            tooltipOption={dataModelLabelInfoTooltipOptions.subjectUnderTable}
          />
        </UnderTableHeaderDiv>
      </RequiredFieldRow>
      {subjectsList.map((subject, ix) => {
        const { isNew, columnName, canExtend, subjectKey } = subject;
        const validation = subjectNameValidatorFunc(subjectKey);
        return (
          <RequiredFieldRow key={`${subjectKey}-${ix}`}>
            <SubjectNameWrapperDiv>
              {subject.isNew ? (
                <EditableTextArea
                  isBold={true}
                  disabled={false}
                  placeholder={''}
                  value={subject.subjectName}
                  validation={validation}
                  onChange={(newName: string) => {
                    updateSubjectName(subjectKey, newName);
                  }}
                  defaultRows={1}
                  startEditable={
                    !validation.safeParse({ value: subject.subjectName })
                      .success
                  }
                />
              ) : (
                <FieldLabel>{subject.subjectName}</FieldLabel>
              )}
            </SubjectNameWrapperDiv>
            <div>
              <DataModelerSelectTableColumn
                viewKey={schemaObj.fact.key}
                tableKey={tableKey}
                availableColumnsFromServer={availableColumnsFromServer}
                isDisabled={!isNew && !canExtend}
                addFieldOnSetColumn={false}
                columnName={columnName ?? undefined}
                setColumn={(column) => {
                  updateSubjectColumn(subjectKey, tableKey, column);
                }}
                fieldTypeFilter={[FieldType.String, FieldType.Integer]}
              />
            </div>
            {isNew && (
              <SquareRemoveButton
                onClick={() => removeNewSubject(subjectKey)}
                content={'Remove Subject'}
              />
            )}
          </RequiredFieldRow>
        );
      })}
      {isEditMode &&
        (!draftSubjectName ? (
          <RequiredFieldRow>
            <NameWithIconWrapper>
              <TextButton onClick={handleAddNewSubject}>
                + Add Subject {subjectRequired && '(required)'}
              </TextButton>
            </NameWithIconWrapper>
          </RequiredFieldRow>
        ) : (
          <RequiredFieldRow>
            <SubjectNameWrapperDiv>
              <InputField
                value={draftSubjectName ?? ''}
                onChange={onDraftSubjectNameChange}
                maxLength={45}
                hasError={!!draftSubjectNameError}
              />
            </SubjectNameWrapperDiv>
            <div>
              <DataModelerSelectTableColumn
                viewKey={schemaObj.fact.key}
                tableKey={tableKey}
                availableColumnsFromServer={availableColumnsFromServer}
                isDisabled={!isEditMode}
                addFieldOnSetColumn={false}
                columnName={undefined}
                placeholder={'Select new subject'}
                setColumn={(column) => {
                  addNewSubject(draftSubjectName, column);
                }}
                fieldTypeFilter={[FieldType.String, FieldType.Integer]}
              />
            </div>
            {draftSubjectNameError && (
              <FieldError type={'custom'} customError={draftSubjectNameError} />
            )}
            <SquareRemoveButton
              onClick={() => setDraftSubjectName(null)}
              content={'Remove Subject'}
            />
          </RequiredFieldRow>
        ))}
    </>
  );
};

export default DataModelerSubjectsSection;
